import {
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    useTheme,
} from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { Colleges, MbaColleges } from '__generated__/graphql'
import IntakeCard from 'candidate/components/IntakeCard'
import { ChoiceBucket } from 'candidate/models/intake'
import React, { useEffect, useState } from 'react'
import NextButton from 'shared/components/NextButton'
import SecondaryButton from 'shared/components/SecondaryButton'
import TertiaryButton from 'shared/components/TertiaryButton'
import { getDropdownMaxHeight } from 'shared/style/constants'
import { getPxAsRem } from 'shared/utils'
import { educationIntakeYears } from 'shared/utils/intakeQuestionTools'
import { AdditionalButtonsContainer } from '../styles'

//TODO look to sync these answer types with BE
export interface GradCollegeAnswer {
    id: number
    college: ChoiceBucket | undefined
    major: ChoiceBucket | undefined
    graduationDate?: string | null
    gpa: ChoiceBucket | undefined
    gpaScale: string
    error?: string
}

interface Props {
    answer?: GradCollegeAnswer[]
    callback: React.Dispatch<any>
    collegeChoices: Partial<Colleges>[]
    mbaCollegeChoices: Partial<MbaColleges>[]
    degreeChoices: ChoiceBucket[]
    gpaChoices: ChoiceBucket[]
    gpaScales: string[]
    prompt: string
    skipCallback?: React.Dispatch<React.SetStateAction<string>>
    skipText?: string
}

const GraduateDegreesQuestion: React.FC<Props> = ({
    answer = [],
    callback,
    collegeChoices,
    mbaCollegeChoices,
    degreeChoices,
    gpaChoices,
    gpaScales,
    prompt,
    skipCallback,
    skipText,
}: Props) => {
    const [entries, setEntries] = useState<GradCollegeAnswer[]>(
        answer.length > 0
            ? answer
            : [
                  {
                      id: 1,
                      error: '',
                      college: undefined,
                      major: undefined,
                      graduationDate: undefined,
                      gpa: undefined,
                      gpaScale: '',
                  },
              ]
    )

    useEffect(() => {
        if (answer.length > 0) {
            setEntries(
                answer.map((entry, index) => ({
                    ...entry,
                    id: index + 1,
                    error: '',
                }))
            )
        }
    }, [answer])

    const addEntry = () => {
        setEntries((prevEntries) => [
            ...prevEntries,
            {
                id: prevEntries.length + 1,
                error: '',
                college: undefined,
                major: undefined,
                graduationDate: undefined,
                gpa: undefined,
                gpaScale: '',
            },
        ])
    }

    const removeEntry = () => {
        if (entries.length > 1) {
            setEntries(entries.slice(0, -1))
        }
    }

    const updateEntry = (
        id: number,
        updatedEntry: Partial<GradCollegeAnswer>
    ) => {
        setEntries((prevEntries) =>
            prevEntries.map((entry) => {
                if (entry.id === id) {
                    const updated = { ...entry, ...updatedEntry }
                    if (
                        updated.gpaScale &&
                        updated.gpa &&
                        parseFloat(updated.gpaScale) <
                            parseFloat(updated.gpa.choice)
                    ) {
                        updated.error =
                            'GPA cannot be higher than the GPA scale'
                    } else {
                        updated.error = ''
                    }
                    return updated
                }
                return entry
            })
        )
    }

    const submitData = () => {
        const response = entries
        callback({ response })
    }

    const years = educationIntakeYears()
    const theme = useTheme()
    const dropdownMaxHeight = getDropdownMaxHeight(theme)

    return (
        <IntakeCard
            question={prompt}
            skipCallback={skipCallback}
            skipText={skipText}
        >
            {entries.map((entry) => (
                <Grid
                    container
                    style={{
                        display: 'inline-block',
                        marginBottom: getPxAsRem(36),
                        marginTop: getPxAsRem(16),
                    }}
                    key={entry.id}
                >
                    {/* Major Select */}
                    <Grid item xs={12} sm={6}>
                        <FormControl
                            style={{
                                marginBottom: getPxAsRem(24),
                            }}
                            fullWidth
                        >
                            <InputLabel id="major_label">
                                Degree Type
                            </InputLabel>
                            <Select
                                id="major"
                                labelId="major_label"
                                label="Degree"
                                value={entry.major?.choice || ''}
                                onChange={(e) => {
                                    const majorSelection = degreeChoices.find(
                                        (choice: ChoiceBucket) =>
                                            choice.choice === e.target.value
                                    )
                                    updateEntry(entry.id, {
                                        major: majorSelection,
                                        college: undefined, // Force users to reselect their institution when changing major, in case one depends on the other.
                                    })
                                }}
                                placeholder="Select an option:"
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: dropdownMaxHeight,
                                        },
                                    },
                                }}
                            >
                                {degreeChoices.map((choice: ChoiceBucket) => (
                                    <MenuItem
                                        key={choice.choice}
                                        value={choice.choice}
                                    >
                                        {choice.choice}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* College/University Select */}
                    <Grid item xs={12} sm={6}>
                        <FormControl
                            style={{
                                marginBottom: getPxAsRem(24),
                            }}
                            fullWidth
                        >
                            <InputLabel id="college_label">
                                College/University
                            </InputLabel>
                            <Select
                                disabled={!entry.major}
                                id="college"
                                labelId="college_label"
                                label="College/University"
                                value={entry.college?.choice || ''}
                                onChange={(e) => {
                                    //TODO this should be storing id
                                    updateEntry(entry.id, {
                                        college: e.target.value
                                            ? { choice: e.target.value }
                                            : undefined,
                                    })
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: dropdownMaxHeight,
                                        },
                                    },
                                }}
                            >
                                {(entry.major?.choice === 'MBA'
                                    ? mbaCollegeChoices
                                    : collegeChoices
                                ).map((college) => (
                                    <MenuItem
                                        key={college.value}
                                        value={college.value}
                                    >
                                        {college.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Graduation Date Picker */}
                    <Grid item xs={12} sm={6}>
                        <FormControl
                            fullWidth
                            style={{
                                marginBottom: '1.5rem',
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                            }}
                        >
                            <InputLabel id="graduation-date-label">
                                Graduation Date
                            </InputLabel>
                            <Select
                                id="graduation-date"
                                labelId="graduation-date-label"
                                label="Graduation Date"
                                value={entry?.graduationDate || ''}
                                onChange={(e) => {
                                    updateEntry(entry.id, {
                                        graduationDate: e.target.value,
                                    })
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: dropdownMaxHeight,
                                        },
                                    },
                                }}
                            >
                                {years.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* GPA & GPA Scale */}
                    <Grid container>
                        <Grid
                            xs={4}
                            sm={2}
                            sx={{
                                pr: getPxAsRem(16),
                            }}
                        >
                            <FormControl fullWidth error={Boolean(entry.error)}>
                                <InputLabel id="gpa_label">
                                    Cumulative GPA
                                </InputLabel>
                                <Select
                                    id="gpa"
                                    labelId="gpa_label"
                                    label="Cumulative GPA"
                                    value={entry.gpa?.choice || ''}
                                    onChange={(
                                        e: SelectChangeEvent<string>
                                    ) => {
                                        const gpaSelection = gpaChoices.find(
                                            (choice: ChoiceBucket) =>
                                                choice.choice === e.target.value
                                        )
                                        updateEntry(entry.id, {
                                            gpa: gpaSelection,
                                        })
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: dropdownMaxHeight,
                                            },
                                        },
                                    }}
                                >
                                    {gpaChoices.map((choice: ChoiceBucket) => (
                                        <MenuItem
                                            key={choice.choice}
                                            value={choice.choice}
                                        >
                                            {choice.choice}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={8} sm={4}>
                            <FormControl error={Boolean(entry.error)} fullWidth>
                                <InputLabel id="gpa-scale_label">
                                    GPA Scale
                                </InputLabel>
                                <Select
                                    id="gpa-scale"
                                    label="GPA Scale"
                                    labelId="gpa-scale_label"
                                    value={entry.gpaScale?.toString() || ''}
                                    onChange={(
                                        e: SelectChangeEvent<string>
                                    ) => {
                                        const selectedGpaScale = e.target.value

                                        updateEntry(entry.id, {
                                            gpaScale: selectedGpaScale,
                                        })
                                    }}
                                >
                                    {gpaScales.map((choice: string) => (
                                        <MenuItem key={choice} value={choice}>
                                            {choice}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {entry.error && (
                                    <FormHelperText>
                                        GPA cannot be higher than the GPA scale
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            ))}
            <AdditionalButtonsContainer>
                {entries.length > 1 && (
                    <TertiaryButton
                        disabled={entries.length <= 1}
                        onClickHandler={removeEntry}
                        title={'Remove Graduate Degree'}
                        style={{ marginRight: '1rem' }}
                    />
                )}
                <SecondaryButton
                    title="Add Additional Graduate Degree"
                    disabled={false}
                    onClickHandler={addEntry}
                />
            </AdditionalButtonsContainer>
            <NextButton
                disabled={entries.some(
                    (entry) =>
                        !entry.college ||
                        !entry.major ||
                        !entry.graduationDate ||
                        !entry.gpa ||
                        !entry.gpaScale ||
                        Boolean(entry.error)
                )}
                onClickHandler={submitData}
            />
        </IntakeCard>
    )
}

export default GraduateDegreesQuestion
